import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Chip,
    Stack,
    useTheme,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import automationService from '../services/automation.service';
import { usePreferences } from '../contexts/PreferencesContext';

const AutomationHistoryPanel = ({ ruleId, enabled, onLastRunLoaded }) => {
    const theme = useTheme();
    const { t } = usePreferences();
    const [executions, setExecutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const observerTarget = useRef(null);
    const loadedRef = useRef(false);

    const loadExecutions = useCallback(async (id, after = null, append = false) => {
        if (!id) return;
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await automationService.getRuleExecutions(id, 20, after);
            const responseData = response.data;

            let executionsData = [];
            let nextCursorValue = null;

            if (Array.isArray(responseData)) {
                executionsData = responseData;
            } else if (responseData?.data) {
                executionsData = responseData.data;
                nextCursorValue = responseData.paging?.next_cursor || null;
            }

            if (append) {
                setExecutions((prev) => [...prev, ...executionsData]);
            } else {
                setExecutions(executionsData);
                if (executionsData.length > 0) {
                    onLastRunLoaded?.(executionsData[0].executed_at);
                }
            }

            setNextCursor(nextCursorValue);
            setHasMore(nextCursorValue !== null);
        } catch (error) {
            console.error('Failed to fetch rule executions', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [onLastRunLoaded]);

    useEffect(() => {
        if (!enabled || !ruleId) return;
        if (loadedRef.current) return;
        loadedRef.current = true;
        setExecutions([]);
        setNextCursor(null);
        setHasMore(false);
        loadExecutions(ruleId);
    }, [enabled, ruleId, loadExecutions]);

    useEffect(() => {
        if (!enabled) {
            loadedRef.current = false;
        }
    }, [enabled]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading && ruleId) {
                    loadExecutions(ruleId, nextCursor, true);
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [ruleId, hasMore, nextCursor, loadingMore, loading, loadExecutions]);

    if (!ruleId) {
        return null;
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (executions.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
                <EventNoteIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="subtitle2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    {t('pages.automations.historyPanel.emptyTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('pages.automations.historyPanel.emptyDesc')}
                </Typography>
            </Box>
        );
    }

    return (
        <List dense sx={{ py: 0 }}>
            {executions.map((ex) => {
                const isSuccess = ex.status === 'SUCCESS' || ex.status === 'success';
                return (
                    <ListItem
                        key={ex.id}
                        sx={{
                            mb: 1,
                            borderRadius: 2,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
                        }}
                    >
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: isSuccess ? 'success.light' : 'error.light', width: 36, height: 36 }}>
                                {isSuccess ? (
                                    <CheckCircleOutlineIcon sx={{ color: 'success.dark', fontSize: 20 }} />
                                ) : (
                                    <ErrorOutlineIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                                )}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={(
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" fontWeight={700}>
                                        {ex.employee_name || t('pages.automations.historyPanel.unknownEmployee')}
                                    </Typography>
                                    <Chip
                                        label={isSuccess
                                            ? t('pages.automations.historyPanel.success')
                                            : t('pages.automations.historyPanel.failed')}
                                        size="small"
                                        color={isSuccess ? 'success' : 'error'}
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                </Stack>
                            )}
                            secondary={(
                                <Box>
                                    {ex.details && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {ex.details}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(ex.executed_at).toLocaleString()}
                                    </Typography>
                                </Box>
                            )}
                        />
                    </ListItem>
                );
            })}
            {hasMore && (
                <Box ref={observerTarget} sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    {loadingMore && <CircularProgress size={22} />}
                </Box>
            )}
        </List>
    );
};

export default AutomationHistoryPanel;
